import React from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { FaUserTie } from 'react-icons/fa';
import ContactCluster, { ContactFace } from './ContactCluster';
import css from './PullTrack.module.scss';

export interface ReachedOutNodeProps {
  value: number;
  faces: ContactFace[];
  ghost: boolean;
  tooltip?: string;
  focused?: boolean;
  to: string;
  onSelect?: () => void;
}

const fmt = (n: number) => n.toLocaleString();

/**
 * The signature station. Hero number is the count of applications we've actually
 * emailed (`totalContacted`); below it, the real faces behind that number. Keeps
 * the ingrained word "Applications" beside the human framing "Reached out".
 */
const ReachedOutNode: React.FC<ReachedOutNodeProps> = ({
  value,
  faces,
  ghost,
  tooltip,
  focused,
  to,
  onSelect,
}) => {
  const ariaLabel = `Reached out: ${fmt(value)} applications emailed${tooltip ? `. ${tooltip}` : ''}`;
  const link = (
    <Link to={to} className={css.main} onClick={onSelect} aria-label={ariaLabel}>
      <span className={`${css.value} ${css.reachedValue}`}>{fmt(value)}</span>
      <span className={css.label}>
        Reached out<span className={css.note}> · Applications</span>
      </span>
      <span className={css.sublabel}>outreach sent</span>
    </Link>
  );

  return (
    <div className={`${css.station} ${css.reachedStation} ${focused ? css.focused : ''}`}>
      <div className={css.nodeRow}>
        <span className={`${css.dot} ${css.dotReached} ${css.reachedPulse}`} aria-hidden="true">
          <FaUserTie className={css.dotReachedGlyph} />
        </span>
      </div>
      <div className={css.body}>
        {tooltip ? <Tooltip title={tooltip}>{link}</Tooltip> : link}
        {ghost ? (
          <>
            <div className={css.ghostRow} aria-hidden="true">
              <span className={css.ghostDot} />
              <span className={css.ghostDot} />
              <span className={css.ghostDot} />
            </div>
            <p className={css.ghostCaption}>The hiring managers you'll reach show up here.</p>
          </>
        ) : (
          <div className={css.cluster}>
            <ContactCluster faces={faces} total={value} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReachedOutNode;
